import { useEffect, useState } from 'react';
import { mobileTeacherApi } from '../../../lib/api';
import { CalendarBlank, ChalkboardTeacher, Bell, ArrowRight, Clock, Warning } from '@phosphor-icons/react';

const DAY_LABELS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const TeacherHome = ({ me, notif, onNavigate }) => {
  const [timetable, setTimetable] = useState({ grouped_by_day: {}, count: 0 });
  const [today, setToday] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await mobileTeacherApi.timetable();
        setTimetable(data);
        const dow = (new Date().getDay() + 6) % 7; // Mon=0, Sun=6
        setToday(data?.grouped_by_day?.[dow] || []);
      } catch { /* silent */ }
    })();
  }, []);

  return (
    <div className="p-4 space-y-4" data-testid="teacher-home">
      {/* Welcome banner */}
      <div className="rounded-xl bg-gradient-to-br from-[#0052FF] to-[#3B82F6] text-white p-5 shadow-lg">
        <h2 className="font-outfit text-lg font-bold">
          Bonjour {me.first_name?.split(' ')[0] || ''} 👋
        </h2>
        <p className="text-sm opacity-90 mt-1">
          {me.classes.length > 0
            ? `${me.classes.length} classe${me.classes.length > 1 ? 's' : ''} assigné${me.classes.length > 1 ? 'es' : 'e'}`
            : 'Aucune classe assignée'}
        </p>
      </div>      {/* Quick actions grid */}
      <div className="grid grid-cols-2 gap-3">
        <QuickCard
          testid="qc-notes"
          icon={ChalkboardTeacher}
          color="#0052FF"
          label="Saisir des notes"
          onClick={() => onNavigate('notes')}
        />
        <QuickCard
          testid="qc-dispo"
          icon={CalendarBlank}
          color="#10B981"
          label="Ma disponibilité"
          onClick={() => onNavigate('availability')}
        />
        <QuickCard
          testid="qc-rdv"
          icon={Bell}
          color="#F59E0B"
          label={notif > 0 ? `${notif} RDV en attente` : 'Rendez-vous'}
          badge={notif > 0}
          onClick={() => onNavigate('availability')}
        />
        <QuickCard
          testid="qc-profile"
          icon={Clock}
          color="#6366F1"
          label={`${timetable.count} séance${timetable.count > 1 ? 's' : ''}/semaine`}
          onClick={() => onNavigate('timetable')}
        />
        {/* iter92 — Self-service: my absences (opens full app route) */}
        <QuickCard
          testid="qc-my-absences"
          icon={Warning}
          color="#F59E0B"
          label="Mes absences"
          onClick={() => { window.location.href = '/m/my-absences'; }}
        />
      </div>

      {/* Today's schedule */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-outfit font-bold text-sm text-[#0F172A]">
            Aujourd&apos;hui — {DAY_LABELS[(new Date().getDay() + 6) % 7]}
          </h3>
        </div>
        {today.length === 0 ? (
          <p className="text-sm text-[#94A3B8] text-center py-4">
            Aucune séance programmée aujourd&apos;hui
          </p>
        ) : (
          <ul className="space-y-2">
            {today.map((t, i) => (
              <li
                key={i}
                className="flex items-center gap-3 p-2 rounded bg-[#F8FAFC] border-l-4 border-[#0052FF]"
                data-testid={`today-session-${i}`}
              >
                <div className="text-xs font-mono text-[#0052FF] font-bold min-w-[80px]">
                  {t.start_time} — {t.end_time}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-outfit font-semibold text-sm truncate">
                    {t.subject || 'Cours'}
                  </div>
                  <div className="text-xs text-[#64748B] truncate">
                    Classe : {t.class_name} {t.room && `• Salle ${t.room}`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Classes list preview */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-outfit font-bold text-sm">Mes classes</h3>
          <button
            onClick={() => onNavigate('notes')}
            className="text-xs text-[#0052FF] font-medium flex items-center gap-1"
            data-testid="see-classes-btn"
          >
            Saisir <ArrowRight size={12} />
          </button>
        </div>
        {me.classes.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">Aucune classe assignée. Contactez l&apos;administration.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {me.classes.map(c => (
              <span
                key={c}
                className="px-3 py-1 rounded-full bg-[#EFF6FF] text-[#0052FF] text-xs font-medium"
                data-testid={`class-chip-${c}`}
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const QuickCard = ({ icon: Icon, color, label, onClick, badge, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="relative text-left bg-white rounded-xl border border-[#E2E8F0] p-4 hover:shadow-md active:scale-[0.98] transition-all"
  >
    <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: `${color}15` }}>
      <Icon size={22} weight="duotone" style={{ color }} />
    </div>
    <div className="text-sm font-outfit font-medium text-[#0F172A] leading-tight">
      {label}
    </div>
    {badge && (
      <span className="absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
    )}
  </button>
);

export default TeacherHome;
